import React, { useEffect, useRef, useState } from 'react';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import { AssistantProvider, useAssistant } from '../../Context/AssistantContext';
import AssistantMessage from './AssistantMessage';
import AssistantSuggestions from './AssistantSuggestions';
import './Assistant.scss';

/* matches the panel's exit animation in Assistant.scss */
const EXIT_MS = 180;

const firstName = (name) => String(name || '').trim().split(/\s+/)[0];

const AssistantPanel = ({ userName }) => {
    const { open, setOpen, sending, messages, inputRef, send } = useAssistant();
    const [draft, setDraft] = useState('');
    /* keeps the panel mounted while its exit animation plays */
    const [shown, setShown] = useState(open);
    const endRef = useRef(null);

    useEffect(() => {
        if (open) { setShown(true); return undefined; }
        const timer = setTimeout(() => setShown(false), EXIT_MS);
        return () => clearTimeout(timer);
    }, [open]);

    useEffect(() => {
        if (open && endRef.current && endRef.current.scrollIntoView) {
            endRef.current.scrollIntoView({ block: 'end', behavior: 'smooth' });
        }
    }, [open, messages, sending]);

    const visible = messages.filter((message) => !message.hidden);
    const started = visible.length > 0;
    const name = firstName(userName);

    const submit = (event) => {
        event.preventDefault();
        if (!draft.trim() || sending) return;
        send(draft);
        setDraft('');
    };

    const pick = (prompt) => {
        if (sending) return;
        send(prompt);
    };

    return (
        <div className="assistant-dock">
            {shown && (
                <section className={`assistant-panel${open ? '' : ' is-leaving'}`} aria-label="Action Agent">
                    <header className="assistant-head">
                        <span className="assistant-avatar" aria-hidden="true"><AutoAwesomeRoundedIcon /></span>
                        <div className="assistant-head-text">
                            <b>Action Agent</b>
                            <span><i className="assistant-online" aria-hidden="true" />Tell me what to do, I'll handle it</span>
                        </div>
                        <button type="button" onClick={() => setOpen(false)} aria-label="Close Action Agent">
                            <CloseRoundedIcon />
                        </button>
                    </header>

                    <div className="assistant-body">
                        {!started && (
                            <div className="assistant-welcome">
                                <span className="assistant-welcome-orb" aria-hidden="true"><AutoAwesomeRoundedIcon /></span>
                                <h3>{name ? `Hi ${name}, what can I do for you?` : 'What can I do for you?'}</h3>
                                <p className="assistant-hint">
                                    Pick a task or just type it. I'll show you exactly what I'm about to do and
                                    wait for your confirmation.
                                </p>
                                <AssistantSuggestions variant="grid" disabled={sending} onPick={pick} />
                            </div>
                        )}
                        {visible.map((message) => (
                            <AssistantMessage key={message.id} message={message} />
                        ))}
                        {sending && (
                            <div className="assistant-msg is-assistant">
                                <div className="assistant-bubble assistant-typing" aria-live="polite">
                                    <span className="assistant-dot" />
                                    <span className="assistant-dot" />
                                    <span className="assistant-dot" />
                                    <span className="assistant-sr">Action Agent is working on it</span>
                                </div>
                            </div>
                        )}
                        <div ref={endRef} />
                    </div>

                    {started && (
                        <AssistantSuggestions variant="row" disabled={sending} onPick={pick} />
                    )}

                    <form className="assistant-form" onSubmit={submit}>
                        <input
                            ref={inputRef}
                            type="text"
                            value={draft}
                            maxLength={2000}
                            placeholder="Ask Action Agent..."
                            aria-label="Message Action Agent"
                            onChange={(e) => setDraft(e.target.value)}
                        />
                        <button type="submit" disabled={sending || !draft.trim()} aria-label="Send">
                            <ArrowUpwardRoundedIcon />
                        </button>
                    </form>
                </section>
            )}

            <button
                type="button"
                className={`assistant-fab${open ? ' is-open' : ''}`}
                onClick={() => setOpen(!open)}
                aria-label="Action Agent"
                aria-expanded={open}
            >
                <span className="assistant-fab-icon is-spark"><AutoAwesomeRoundedIcon /></span>
                <span className="assistant-fab-icon is-close"><CloseRoundedIcon /></span>
            </button>
        </div>
    );
};

/* Mounted in the authenticated layout (Header). Rendered only when the
   flag is on and the signed-in user has an employee record; the server guard
   is the authority either way. The flag is read at render, not at import. */
export const AssistantGate = ({ user }) => {
    const enabled = process.env.REACT_APP_ASSISTANT_ENABLED === 'true';
    if (!enabled || !(user && user.employeeCode)) return null;
    return (
        <AssistantProvider>
            <AssistantPanel userName={user.employeeName} />
        </AssistantProvider>
    );
};

export { AssistantPanel };
export default AssistantGate;
